import { deriveSdkLogger } from '../logging/derive-sdk-logger'

const REQUEST_TIMEOUT_MS = 10_000
const logger = deriveSdkLogger(undefined, {
  component: 'ToolApprovalRequest',
})

export enum ToolApprovalStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  EXPIRED = 'EXPIRED',
}

export interface ToolApprovalRequest {
  id: string
  toolCallId: string
  sessionId: string
  policyId: string
  policyVersionId: string
  toolName: string
  agentId: string
  status: ToolApprovalStatus
  organizationId: string
  requestedAt: Date
  decidedAt: Date | null
  decidedByUserId: string | null
  decisionNotes: string | null
}

export interface ToolApprovalRequestInput {
  toolCallId: string
  policyId: string
  policyVersionId: string
  toolName: string
  toolId?: string | null
  toolVersionId?: string | null
}

export interface ToolApprovalRequestStatusUpdateResult {
  updated: boolean
  result: ToolApprovalRequest | null
}

type ToolApprovalRequestLookup = Record<string, ToolApprovalRequest | null>
type ToolApprovalRequestUpdateLookup = Record<
  string,
  ToolApprovalRequestStatusUpdateResult | null
>

function getTimeoutSignal(): AbortSignal | undefined {
  if (typeof AbortSignal === 'undefined') {
    return undefined
  }

  return typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
    : undefined
}

function buildEndpoint(apiBaseUrl: string, path: string): string {
  return new URL(path, apiBaseUrl).toString()
}

function createHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseDate(value: unknown, fieldName: string): Date {
  if (typeof value !== 'string') {
    throw new TypeError(`Expected ${fieldName} to be a string`)
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    throw new TypeError(`Expected ${fieldName} to be a valid ISO date string`)
  }

  return parsed
}

export function parseToolApprovalRequest(data: unknown): ToolApprovalRequest {
  if (!isRecord(data)) {
    throw new TypeError('Expected tool approval request to be an object')
  }

  if (typeof data.id !== 'string') {
    throw new TypeError('Expected id to be a string')
  }
  if (typeof data.toolCallId !== 'string') {
    throw new TypeError('Expected toolCallId to be a string')
  }
  if (typeof data.sessionId !== 'string') {
    throw new TypeError('Expected sessionId to be a string')
  }
  if (typeof data.policyId !== 'string') {
    throw new TypeError('Expected policyId to be a string')
  }
  if (typeof data.policyVersionId !== 'string') {
    throw new TypeError('Expected policyVersionId to be a string')
  }
  if (typeof data.toolName !== 'string') {
    throw new TypeError('Expected toolName to be a string')
  }
  if (typeof data.agentId !== 'string') {
    throw new TypeError('Expected agentId to be a string')
  }
  if (typeof data.organizationId !== 'string') {
    throw new TypeError('Expected organizationId to be a string')
  }
  if (typeof data.status !== 'string') {
    throw new TypeError('Expected status to be a string')
  }
  if (!(data.status in ToolApprovalStatus)) {
    throw new TypeError(`Unexpected tool approval status: ${data.status}`)
  }

  return {
    id: data.id,
    toolCallId: data.toolCallId,
    sessionId: data.sessionId,
    policyId: data.policyId,
    policyVersionId: data.policyVersionId,
    toolName: data.toolName,
    agentId: data.agentId,
    status: ToolApprovalStatus[data.status as keyof typeof ToolApprovalStatus],
    organizationId: data.organizationId,
    requestedAt: parseDate(data.requestedAt, 'requestedAt'),
    decidedAt:
      data.decidedAt == null ? null : parseDate(data.decidedAt, 'decidedAt'),
    decidedByUserId:
      data.decidedByUserId == null ? null : String(data.decidedByUserId),
    decisionNotes:
      data.decisionNotes == null ? null : String(data.decisionNotes),
  }
}

async function postJson<TPayload>(
  endpoint: string,
  payload: TPayload,
  accessToken: string,
): Promise<Response> {
  return fetch(endpoint, {
    method: 'POST',
    headers: createHeaders(accessToken),
    body: JSON.stringify(payload),
    signal: getTimeoutSignal(),
  })
}

async function safeReadErrorBody(response: Response): Promise<string> {
  try {
    return await response.text()
  } catch {
    return ''
  }
}

export async function getOrCreateToolApprovalRequest({
  toolCallId,
  toolName,
  toolId,
  toolVersionId,
  policyId,
  policyVersionId,
  sessionId,
  apiBaseUrl,
  accessToken,
}: {
  toolCallId: string
  toolName: string
  toolId?: string | null
  toolVersionId?: string | null
  policyId: string
  policyVersionId: string
  sessionId: string
  apiBaseUrl: string
  accessToken: string
}): Promise<ToolApprovalRequest | null> {
  const endpoint = buildEndpoint(
    apiBaseUrl,
    '/api/tool_approval_requests/get_or_create',
  )
  const payload = {
    toolCallId,
    policyId,
    policyVersionId,
    sessionId,
    toolName,
    ...(toolId == null ? {} : { toolId }),
    ...(toolVersionId == null ? {} : { toolVersionId }),
  }

  try {
    logger.debug?.(`Creating tool approval request for tool call ${toolCallId}`)
    const response = await postJson(endpoint, payload, accessToken)
    if (!response.ok) {
      logger.error?.(
        `Failed to create tool approval request: HTTP ${response.status} - ${await safeReadErrorBody(response)}`,
      )
      return null
    }

    return parseToolApprovalRequest(await response.json())
  } catch (error) {
    logger.error?.(
      `Failed to create tool approval request: ${error instanceof Error ? error.message : String(error)}`,
    )
    return null
  }
}

export async function getOrCreateManyToolApprovalRequests({
  toolApprovalRequestInputs,
  sessionId,
  apiBaseUrl,
  accessToken,
}: {
  toolApprovalRequestInputs: Iterable<ToolApprovalRequestInput>
  sessionId: string
  apiBaseUrl: string
  accessToken: string
}): Promise<ToolApprovalRequestLookup> {
  const inputsList = Array.from(toolApprovalRequestInputs)
  if (inputsList.length === 0) {
    return {}
  }

  const requestedToolCallIds = inputsList.map((input) => input.toolCallId)
  const approvalRequestsLookup = Object.fromEntries(
    requestedToolCallIds.map((toolCallId) => [toolCallId, null]),
  ) as ToolApprovalRequestLookup

  const endpoint = buildEndpoint(
    apiBaseUrl,
    '/api/tool_approval_requests/get_or_create_many',
  )
  const payload = {
    inputs: inputsList.map((input) => ({
      toolCallId: input.toolCallId,
      policyId: input.policyId,
      policyVersionId: input.policyVersionId,
      toolName: input.toolName,
      toolId: input.toolId ?? null,
      toolVersionId: input.toolVersionId ?? null,
    })),
    agentId: 'unknown',
    organizationId: 'unknown',
    sessionId,
    requestedAt: null,
  }

  try {
    logger.debug?.(
      `Batch creating tool approval requests for ${requestedToolCallIds.length} tool calls`,
    )
    const response = await postJson(endpoint, payload, accessToken)
    if (!response.ok) {
      logger.error?.(
        `Failed to batch create tool approval requests: HTTP ${response.status} - ${await safeReadErrorBody(response)}`,
      )
      return approvalRequestsLookup
    }

    const responseJson = await response.json()
    if (!Array.isArray(responseJson)) {
      throw new TypeError(
        'Expected list response from get_or_create_many endpoint',
      )
    }

    for (const [index, item] of responseJson.entries()) {
      try {
        const parsed = parseToolApprovalRequest(item)
        approvalRequestsLookup[parsed.toolCallId] = parsed
      } catch (error) {
        logger.error?.(
          `Failed to parse tool approval request at index ${index} from batch response: ${error instanceof Error ? error.message : String(error)}`,
        )
      }
    }

    return approvalRequestsLookup
  } catch (error) {
    logger.error?.(
      `Failed to batch create tool approval requests: ${error instanceof Error ? error.message : String(error)}`,
    )
    return approvalRequestsLookup
  }
}

export async function cancelManyToolApprovalRequests({
  approvalRequestIds,
  apiBaseUrl,
  accessToken,
}: {
  approvalRequestIds: string[]
  apiBaseUrl: string
  accessToken: string
}): Promise<ToolApprovalRequestUpdateLookup> {
  if (approvalRequestIds.length === 0) {
    return {}
  }
  const updateResultsByRequestId = Object.fromEntries(
    approvalRequestIds.map((approvalRequestId) => [approvalRequestId, null]),
  ) as ToolApprovalRequestUpdateLookup

  const endpoint = buildEndpoint(
    apiBaseUrl,
    '/api/tool_approval_requests/cancel_many',
  )

  try {
    logger.debug?.(
      `Cancelling ${approvalRequestIds.length} tool approval requests`,
    )
    const response = await postJson(
      endpoint,
      { ids: approvalRequestIds },
      accessToken,
    )
    if (!response.ok) {
      logger.error?.(
        `Failed to cancel tool approval requests: HTTP ${response.status} - ${await safeReadErrorBody(response)}`,
      )
      return updateResultsByRequestId
    }

    const responseJson = await response.json()
    if (!Array.isArray(responseJson)) {
      throw new TypeError('Expected list response from cancel_many endpoint')
    }

    for (const [index, item] of responseJson.entries()) {
      try {
        if (!isRecord(item)) {
          throw new TypeError('Expected dict items in cancel_many response')
        }

        const updated = Boolean(item.updated)
        const result =
          item.result == null ? null : parseToolApprovalRequest(item.result)
        const parsedUpdateResult: ToolApprovalRequestStatusUpdateResult = {
          updated,
          result,
        }

        if (parsedUpdateResult.result) {
          updateResultsByRequestId[parsedUpdateResult.result.id] =
            parsedUpdateResult
        }
      } catch (error) {
        logger.error?.(
          `Failed to parse tool approval request cancellation response at index ${index}: ${error instanceof Error ? error.message : String(error)}`,
        )
      }
    }

    return updateResultsByRequestId
  } catch (error) {
    logger.error?.(
      `Failed to cancel tool approval requests: ${error instanceof Error ? error.message : String(error)}`,
    )
    return updateResultsByRequestId
  }
}
